var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useImperativeHandle } from 'react';
import shortid from 'shortid';
import { debounce } from 'lodash-es';
import styled, { css } from '../../styled';
import { generateStatusStyle, MessageWrapper, Message } from './index';
import Text from '../text';
const Title = styled(Text) `
  padding-button: 0.25rem;
  display: block;
`;
const StyledMessage = styled(Message) `
  line-height: 1rem;
  ${props => props.status === 'invalid' &&
    css `
      color: ${({ theme }) => theme.colors.RED_700};
    `}
`;
const ContentWrapper = styled.label `
  display: inline-flex;
  flex-direction: column;
  box-sizing: border-box;
  border-radius: 3px;

  &:focus-within {
    ${({ status, disabled }) => generateStatusStyle(status, true, disabled, true)};
  }
  &:not(:focus-within) {
    ${({ status, disabled }) => generateStatusStyle(status, true, disabled, false)};
  }

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
  ${({ block }) => block &&
    css `
      display: flex;
      width: 100%;
    `}
  ${({ block }) => !block &&
    css `
      display: inline-flex;
    `}
`;
export const StyledTextArea = styled.textarea `
  background-color: transparent;
  cursor: inherit;
  outline: none;
  box-sizing: border-box;
  resize: none;
  font-size: ${({ fontSize }) => fontSize || '1rem'};
  line-height: 1.5rem;
  width: ${({ width }) => width}rem;
  padding: 0.5rem 0.75rem;

  &::placeholder {
    color: ${({ theme }) => theme.colors.INK_400};
  }
  ${({ block }) => block &&
    css `
      display: block;
      width: 100%;
    `};
  border-radius: 3px;
  border: none;
  ${({ withCount, minHeight, maxHeight }) => css `
      height: ${minHeight - (withCount ? 2 : 0)}rem;
      min-height: ${minHeight - (withCount ? 2 : 0)}rem;
      max-height: ${maxHeight - (withCount ? 2 : 0)}rem;
      padding-bottom: 0;
    `};
`;
const Count = styled(Text) `
  display: inline-block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  text-align: end;
  box-sizing: border-box;
  line-height: 1rem;
`;
const KEY_CODE = { ENTER: 13, ESCAPE: 27 };
const UITextArea = (_a) => {
    var { title, disabled, status = 'valid', value, renderMessages = [], innerRef, block = false, width = 10, maxCount, minHeight = maxCount ? 4.125 : 3.5, maxHeight = 7.5, lang = 'en', 
    // TODO: Confirm the prop `rows` has no use anywhere
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rows = 1, count, onEnterPress, onEscapePress, onKeyDown } = _a, restProps = __rest(_a, ["title", "disabled", "status", "value", "renderMessages", "innerRef", "block", "width", "maxCount", "minHeight", "maxHeight", "lang", "rows", "count", "onEnterPress", "onEscapePress", "onKeyDown"]);
    const inputRef = React.useRef(null);
    // @ts-ignore
    useImperativeHandle(innerRef, () => inputRef.current);
    const titles = title && (React.createElement(Title, { fontType: "Body", lang: lang, color: "INK_800" }, title));
    const handleOnKeyDown = (event) => {
        if (event.keyCode === KEY_CODE.ENTER &&
            !event.shiftKey &&
            inputRef.current &&
            onEnterPress) {
            onEnterPress(event);
            return;
        }
        if (event.keyCode === KEY_CODE.ESCAPE &&
            inputRef.current &&
            onEscapePress) {
            onEscapePress(event);
            return;
        }
        if (onKeyDown) {
            onKeyDown(event);
        }
    };
    const updateHeight = React.useCallback(debounce(() => {
        const { current } = inputRef;
        if (current) {
            // eslint-disable-next-line no-param-reassign
            current.style.height = '0px';
            // eslint-disable-next-line no-param-reassign
            current.style.height = `${current.scrollHeight}px`;
        }
    }), [inputRef]);
    useEffect(() => {
        updateHeight();
    }, [inputRef, updateHeight, value]);
    const content = (React.createElement(ContentWrapper, { block: block, status: status, disabled: disabled },
        React.createElement(StyledTextArea, Object.assign({ rows: 1, ref: inputRef, value: value, status: status, disabled: disabled, block: block, withCount: !!maxCount, onChange: e => {
                if (restProps.onChange) {
                    restProps.onChange(e);
                }
                updateHeight();
            }, onKeyDown: handleOnKeyDown, width: width, minHeight: minHeight, maxHeight: maxHeight }, restProps)),
        !!maxCount && (count || count === 0) && (React.createElement(Count, { lang: lang, fontType: "Micro", color: count <= maxCount ? 'INK_500' : 'RED_700' }, maxCount - count))));
    const messages = (React.createElement(MessageWrapper, null, renderMessages.map(renderMessage => (React.createElement(StyledMessage, { status: status, withBullet: renderMessages.length > 1, key: shortid.generate() },
        React.createElement(Text, { lang: lang, fontType: "Caption" }, renderMessage()))))));
    return (React.createElement(React.Fragment, null,
        titles,
        content,
        messages));
};
export default React.memo(React.forwardRef((props, ref) => React.createElement(UITextArea, Object.assign({ innerRef: ref }, props))));
//# sourceMappingURL=UITextArea.js.map